import React, { useCallback, useEffect, useState } from 'react'
import { PositionCard, ScrollTop, PositionFilters, PositionsListOrStack, ScrollBottom } from 'components'
import { Box, Button, Fab, Stack, Tooltip, Typography } from '@mui/material'
import { useSessionContext } from 'session'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate, useParams } from 'react-router-dom'
import {
  type Position,
  type PositionFilterResults,
  positionListOrg,
  type PositionOrganization,
  type PositionType
} from 'services'
import { newPosition } from 'helpers'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'

export interface PositionsFilter {
  positionTypes: PositionType[]
  email: string | null
  title: string | null
  firstName: string | null
  lastName: string | null
  vacant: string
}
export const PositionsPage: React.FC = () => {
  const MAX_POSITIONS_SINGLE_PAGE = 50

  const { changeModuleName, changeUniqueId } = useSessionContext()
  const { orgId } = useParams<{ orgId: string }>()
  const parsedOrgId = orgId != null ? parseInt(orgId, 10) : 0
  const [uniqueIdOrg, setUniqueIdOrg] = useState<string>('')
  const [positions, setPositions] = useState<PositionFilterResults>({
    page_number: 1,
    page_size: MAX_POSITIONS_SINGLE_PAGE,
    total_items: 0,
    total_pages: 1,
    data: []
  })
  const initialState: PositionsFilter = {
    firstName: '',
    lastName: '',
    title: '',
    vacant: ' ',
    email: '',
    positionTypes: []
  }
  const [filters, setFilters] = useState<PositionsFilter>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<PositionsFilter>(initialState)
  const [positionToAdd, setPositiontoAdd] = useState<Position | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [reloadPositions, setReloadPositions] = useState<boolean>(false)
  const navigate = useNavigate()
  const [paginatedMode, setPaginatedMode] = useState(false)

  useEffect(() => {
    if (uniqueIdOrg !== '') {
      changeModuleName('Positions for Organization')
      changeUniqueId(uniqueIdOrg)
      document.title = `Org Positions ${uniqueIdOrg}`
    }
  }, [uniqueIdOrg])

  const getUniqueIdFromOrganization = (organization: PositionOrganization | undefined): string => {
    return `${parsedOrgId} - ${organization?.uniqueId ?? ''} - ${organization?.organizationName ?? ''}`
  }

  useEffect(() => {
    const listPositionsStack = async (): Promise<void> => {
      setLoading(true)
      const value = await positionListOrg(parsedOrgId, positions.page_number, positions.page_size, filters)
      setLastQueriedFilters(filters)
      if (value?.data.length > 0) {
        const { organization } = value.data[0]
        setUniqueIdOrg(getUniqueIdFromOrganization(organization))
      }
      if (positions.total_pages > 1 || value.total_items > MAX_POSITIONS_SINGLE_PAGE) {
        setPaginatedMode(true)
      }
      setPositions(value)
      setLoading(false)
    }

    void listPositionsStack()
  }, [reloadPositions])

  const createAddPosition = async (): Promise<void> => {
    setLoading(true)
    newPosition(parsedOrgId).then(value => {
      setTimeout(function () {
        window.scrollTo(0, document.body.scrollHeight)
      }, 100)
      setPositiontoAdd(value)
    }).finally(() => { setLoading(false) })
  }

  const clearPositionToAdd = (): void => {
    setPositiontoAdd(null)
  }

  const triggerReload = useCallback(async (): Promise<void> => {
    setReloadPositions(prevState => !prevState)
  }, [])

  const updateFilters = useCallback(
    async (filters: any): Promise<void> => {
      setFilters((prevFilters) => ({ ...prevFilters, ...filters }))
    },
    [])

  const handleChangePage = useCallback(async (page: number): Promise<void> => {
    setPositions((prev) => ({ ...prev, page_number: page }))
    void triggerReload()
  }, [])

  const handleDoFilter = useCallback(async (): Promise<void> => {
    setPositions((prev) => ({ ...prev, page_number: 1 }))
    void triggerReload()
  }, [])

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) {
      setReloadPositions(prevState => !prevState)
    }
  }

  return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box width='95%' sx={{ pb: 1 }}>
                    <Box id='titleAdd' sx={{ ml: '-2vw', mt: '0.5vh' }}>
                        <Button variant="contained" size='large' onClick={() => { navigate(`/editOrganization/${parsedOrgId}/`) }}> <ArrowBackIosNewRoundedIcon/>
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                      <Tooltip title="Add Position">
                        <span>
                          <Fab color="primary" aria-label="add" onClick={() => { void createAddPosition() }}>
                              <AddIcon/>
                          </Fab>
                        </span>
                      </Tooltip>
                    </Box>
                    <Stack spacing={2}>
                      <Typography style={{ flex: '1' }}>{`Position Count:${positions.total_items}`}</Typography>
                      {paginatedMode && <PositionFilters filters={filters} updateFilters={updateFilters} doFilter={handleDoFilter} cleanFilters={cleanFilter}/>}
                      <PositionsListOrStack positions={positions} loading={loading} parsedOrgId={parsedOrgId} triggerReload={triggerReload} paginatedMode={paginatedMode} changePage={handleChangePage}></PositionsListOrStack>
                      {positionToAdd != null && <PositionCard index={positions.data.length + 1} originalPosition={positionToAdd} createCard={true} triggerReload={triggerReload} clearPositionToAdd={clearPositionToAdd} displayOrgLink={false}/>}
                    </Stack>
                </Box>
              <ScrollTop/>
              <ScrollBottom/>
            </Box>
        </Box>
  )
}
